import { safeRedirectTarget } from "~/utils/sanitize-redirect-url";
import { getServerClient, graphql } from "./graphql";
import { ConfigContext, WebConfig } from "./hooks/webconfig";
import styles from "./tailwind.css?url";

const MindstoneConfigDocument = graphql(`
  query MindstoneConfig {
    config {
      siteURL
      api
      appUrl
      identityUrl
      rudderstack {
        url
        writeKey
      }
      oauth {
        apple
        google
        linkedIn
        microsoft
        microsoftClientId
      }
      environment
      growthbookKey
      googleMapKey
    }
  }
`);

import { data, LoaderFunctionArgs, redirect } from "react-router";
import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useRouteError,
} from "react-router";
import * as Sentry from "@sentry/react";
import * as jose from "jose";
import React, { useEffect, useState } from "react";
import {
  GenericCatchBoundary,
  GenericErrorBoundary,
} from "~/components/error-boundaries/error-boundaries";
import { rootGetAccessToken } from "~/utils/ensure-authenticated.server";
import {
  clearRedirectHopCookie,
  readRedirectHopCount,
  REDIRECT_HOP_LIMIT,
  serializeRedirectHopCookie,
  shouldBreakRedirectLoop,
} from "~/utils/redirect-loop-guard";
import { RedirectLoopBlocked } from "~/components/redirect-loop-blocked/redirect-loop-blocked";
import { useStopReplayOnOauthNavigation } from "~/utils/oauth-replay-guard";
// import {
//   Notification,
//   NotificationProps,
// } from "./components/common/notification";
import {
  accessTokenCookie,
  csrfProtectionCookie,
  loggedInCookie,
  attributionCookie,
  getAttributionTrackingData,
} from "~/utils/oauth.server";
import { Toaster } from "@mindstonehq/ui";
import metrics from "~/utils/metrics";
import { ApolloHydrationHelper } from "@apollo/client-integration-react-router";
import { I18nProvider } from "@lingui/react";
import { activateLocale, i18n } from "./i18n/i18n";
import { detectFromAcceptLanguage, parseLocaleCookie } from "~/utils/i18n";
import {
  GoogleTagManagerBody,
  GoogleTagManagerHead,
} from "~/components/google-tag-manager";
import { redactOauthUrls } from "~/utils/oauth-url-redaction";

function redactNullableOauthUrl(value: string | null): string | null {
  return value === null ? null : redactOauthUrls(value);
}

export function links() {
  return [
    { rel: "icon", type: "image/png", href: "/favicon.ico" },
    {
      rel: "icon",
      type: "image/png",
      sizes: "32x32",
      href: "/favicon-32x32.png",
    },
    {
      rel: "icon",
      type: "image/png",
      sizes: "16x16",
      href: "/favicon-16x16.png",
    },
    { rel: "stylesheet", href: styles },
    { rel: "stylesheet", href: "https://rsms.me/inter/inter.css" },
    {
      rel: "stylesheet",
      href: "https://fonts.googleapis.com/css2?family=Figtree:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,300;1,400;1,500;1,600;1,700;1,800&display=swap",
    },
  ];
}

// Same allowlist as every other post-login destination (FOX-4690): this was a
// second suffix match that also let http: and `//host` through.
function resolveAuthedRedirectTarget(
  request: Request,
  fallbackAppUrl: string,
): string {
  const raw = new URL(request.url).searchParams.get("redirectUrl");
  if (!raw) return fallbackAppUrl;
  return safeRedirectTarget(raw) ?? fallbackAppUrl;
}

export async function loader({ request }: LoaderFunctionArgs) {
  const configPromise = getServerClient().query({
    query: MindstoneConfigDocument,
    fetchPolicy: "no-cache",
  });

  const configResult = await configPromise;
  if (configResult.error || !configResult.data) {
    console.error(configResult.error);
    throw new Error("Something went wrong");
  }
  const config = configResult.data.config;
  // Prefer the learner's explicit pick (locale cookie) over browser detection
  // so a choice made on the signup banner carries into onboarding.
  const detectedLanguage =
    parseLocaleCookie(request.headers.get("cookie")) ??
    detectFromAcceptLanguage(request.headers.get("accept-language"));
  const accessTokenResult = await rootGetAccessToken(request);
  let accessToken = "";
  let isNewAccessToken = false;
  if (accessTokenResult) {
    accessToken = accessTokenResult.accessToken;
    isNewAccessToken = accessTokenResult.new;
  }

  // Attribution tracking
  const attributionData = getAttributionTrackingData(request.url);
  const headers = new Headers();
  if (attributionData) {
    headers.append(
      "Set-Cookie",
      await attributionCookie.serialize(attributionData),
    );
  }
  if (isNewAccessToken) {
    headers.append("Set-Cookie", accessTokenCookie(accessToken));
    headers.append("Set-Cookie", loggedInCookie());
  }
  headers.append("Set-Cookie", csrfProtectionCookie(request));

  const shouldReflect =
    !!accessToken &&
    !request.url.includes("callback") &&
    !request.url.includes("onboarding") &&
    // Desktop authorization must reach its child loaders: authorize creates
    // the stored grant and a cookied user completes it immediately; completion
    // delivers the one-time code to loopback rather than config.appUrl.
    // MCP OAuth (tmcp U1): a signed-in user must reach the consent page (a
    // document route); authorize is a resource route, excluded for parity.
    ![
      "/desktop/authorize",
      "/desktop/complete",
      "/oauth/authorize",
      "/oauth/consent",
    ].includes(new URL(request.url).pathname);

  // FOX-3618 — redirect-loop breaker. Identity reflects an authenticated user
  // back to any `.mindstone.com` redirectUrl; if that host bounces them straight
  // back (e.g. a valid session but no access to the target space), the two hosts
  // ping-pong forever. Count reflections in a short window and, once we have
  // reflected this browser REDIRECT_HOP_LIMIT times, stop reflecting and render
  // a terminal page instead of emitting another 302. A normal login reflects
  // exactly once, so this is invisible outside a genuine loop.
  const redirectHops = readRedirectHopCount(request);
  const webConfig = {
    API: config.api,
    graphqlAPI: process.env.GRAPHQL_API,
    appUrl: config.appUrl, // TODO: This will become dynamic
    identityUrl: config.identityUrl,
    rudderstackURL: config.rudderstack?.url,
    rudderstackWriteKeyWebApp: config.rudderstack?.writeKey,
    revision: process.env.REVISION,
    oauthAppleRedirectUrl: config.oauth?.apple.replaceAll(
      "app.",
      "identity.",
    ),
    oauthGoogleRedirectUrl: config.oauth?.google.replaceAll(
      "app.",
      "identity.",
    ),
    oauthLinkedinRedirectUrl: config.oauth?.linkedIn.replaceAll(
      "app.",
      "identity.",
    ),
    oauthMicrosoftRedirectUrl: (config.oauth?.microsoft ?? "").replaceAll(
      "app.",
      "identity.",
    ),
    // Empty when Microsoft sign-in is not configured for this environment:
    // the login/signup routes then pass no handleMicrosoft and the button is
    // not rendered (Rebel migration Stage 6, L2).
    oauthMicrosoftClientId: config.oauth?.microsoftClientId ?? "",
    environment: config.environment,
    growthbookKey: config.growthbookKey,
    googleApiKey: config.googleMapKey,
  } as WebConfig;
  if (shouldReflect) {
    if (shouldBreakRedirectLoop(redirectHops)) {
      // Reset the counter so a deliberate retry starts fresh, and render the
      // terminal page on the identity host (no further 302 — this breaks the
      // loop by construction).
      headers.append("Set-Cookie", clearRedirectHopCookie());
      // Defense-in-depth: never let a CDN/edge cache the loop-blocked page and
      // serve it to a non-looping user. The terminal 200 also carries a
      // Set-Cookie (above) so it should never be cached anyway.
      headers.set("Cache-Control", "no-store");
      // The reflect branch only runs for an authenticated session, so a subject
      // id is derivable — include it so a prod lockout wave can be cross-
      // referenced to affected users/rosters. Fall back to a sentinel if the
      // token can't be decoded (never crash the loop-breaker on a bad token).
      let subject = "unknown";
      try {
        const sub = jose.decodeJwt(accessToken).sub;
        if (sub) subject = sub;
      } catch {
        // leave sentinel
      }
      const requestUrl = new URL(request.url);
      console.warn(
        JSON.stringify({
          msg: "identity redirect-loop breaker tripped",
          fox: "FOX-3618",
          subject,
          host: requestUrl.host,
          // MCP OAuth (tmcp U1, Codex r1 G-F1): an /oauth/* URL keeps its
          // path but not its query (a consent txn); others are unchanged.
          redirectUrl: redactNullableOauthUrl(requestUrl.searchParams.get("redirectUrl")),
          hops: redirectHops,
          limit: REDIRECT_HOP_LIMIT,
          url: redactOauthUrls(request.url),
        }),
      );
      // FOX-4683 — the terminal page still needs window.webConfig: the client
      // entry and the client GraphQL module read it on load, so without it
      // the page never hydrates.
      return data(
        { redirectLoopBlocked: true as const, config: webConfig },
        { headers, status: 200 },
      );
    }
    headers.append("Set-Cookie", serializeRedirectHopCookie(redirectHops + 1));
    const redirectTarget = resolveAuthedRedirectTarget(request, config.appUrl);
    return redirect(redirectTarget, {
      headers,
      status: 302,
    });
  }

  // Not reflecting (login form, logout, callback, onboarding, or unauthenticated):
  // this is a successful, non-looping navigation — clear any stale hop counter so
  // the next login starts from zero.
  if (redirectHops > 0) {
    headers.append("Set-Cookie", clearRedirectHopCookie());
  }

  return data(
    {
      redirectLoopBlocked: false as const,
      config: webConfig,
      detectedLanguage,
    },
    {
      headers,
    },
  );
}
type RootLoaderData =
  | { redirectLoopBlocked: true; config: WebConfig }
  | {
      redirectLoopBlocked: false;
      config: WebConfig;
      detectedLanguage: string;
    };

function App() {
  const loaderData = useLoaderData() as RootLoaderData;
  // Codex r2 R2-F1: no Sentry Replay recording inside an MCP OAuth flow.
  useStopReplayOnOauthNavigation();
  const webConfig = loaderData.redirectLoopBlocked
    ? undefined
    : loaderData.config;
  const detectedLanguage = loaderData.redirectLoopBlocked
    ? undefined
    : loaderData.detectedLanguage;

  useEffect(() => {
    if (webConfig) metrics.init(webConfig);
  }, [webConfig]);

  const [theme, setTheme] = useState(() => {
    return "light";
  });

  const lang = activateLocale(detectedLanguage ?? "en");

  // FOX-3618 — the redirect-loop breaker tripped: render a terminal page on the
  // identity host (no config/Outlet, no further redirect) so the loop cannot
  // continue.
  if (loaderData.redirectLoopBlocked) {
    return (
      <html lang={lang} className={theme}>
        <head>
          <Meta />
          <meta charSet="utf-8" />
          <meta name="viewport" content="width=device-width,initial-scale=1" />
          <Links />
          <WebConfigScript webConfig={loaderData.config} />
        </head>
        <body className="min-h-[100dvh] bg-background text-foreground">
          <I18nProvider i18n={i18n}>
            <RedirectLoopBlocked />
            <Toaster />
          </I18nProvider>
          <ScrollRestoration />
          <Scripts />
        </body>
      </html>
    );
  }

  // Marketing's GTM container — prod only. See components/google-tag-manager.tsx.
  const googleTagManagerEnabled =
    loaderData.config.environment === "production";

  return (
    <html lang={lang} className={theme}>
      <head>
        {googleTagManagerEnabled && <GoogleTagManagerHead />}
        <Meta />
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />
        <Links />
        <OurScripts webConfig={loaderData.config} />
      </head>
      <body className="min-h-[100dvh] bg-background text-foreground">
        {googleTagManagerEnabled && <GoogleTagManagerBody />}
        <I18nProvider i18n={i18n}>
          <ConfigContext.Provider value={loaderData.config}>
            <ApolloHydrationHelper>
              <Outlet />
            </ApolloHydrationHelper>
          </ConfigContext.Provider>
          {/* Toaster MUST stay inside <I18nProvider>: Sonner renders toast
              content in this subtree, so a translated toast outside the
              provider reads a null Lingui context and hard-crashes in a prod
              build. (Preventive — mirrors the web.space fix in
              docs/plans/260616_fix-space-live-i18n-crash.) */}
          <Toaster />
        </I18nProvider>
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export function ErrorBoundary() {
  const error = useRouteError();

  const [theme, setTheme] = useState("light");

  useEffect(() => {
    const item = localStorage.getItem("ms-theme");
    setTheme(item || "light");
  }, []);

  useEffect(() => {
    console.error(error);
    Sentry.captureException(error);
  }, [error]);

  return (
    <html lang="en" className={theme}>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />
        <Meta />
        <Links />
      </head>
      <body className="min-h-[100dvh] bg-theme-background-grey-primary grow flex flex-col lg:flex-row overflow-auto text-theme-text-primary">
        <I18nProvider i18n={i18n}>
          {isRouteErrorResponse(error) ? (
            <GenericCatchBoundary error={error} />
          ) : (
            <GenericErrorBoundary
              error={
                error instanceof Error ? error : new Error("Unknown Error")
              }
            />
          )}
        </I18nProvider>
        <ScrollRestoration />
      </body>
    </html>
  );
}

export default App;

const Trackers = () => {
  return (
    <>
      <noscript>
        <img
          height="1"
          width="1"
          style={{ display: "none" }}
          src="https://www.facebook.com/tr?id=2676983045894747&ev=PageView&noscript=1"
        />
      </noscript>
      <script
        dangerouslySetInnerHTML={{
          __html: `
            (function Load3rdPartyScripts() {
              !function(f,b,e,v,n,t,s)
              {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
                      n.callMethod.apply(n,arguments):n.queue.push(arguments)};
                if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
                n.queue=[];t=b.createElement(e);t.async=!0;
                t.src=v;s=b.getElementsByTagName(e)[0];
                s.parentNode.insertBefore(t,s)}(window, document,'script',
                      'https://connect.facebook.net/en_US/fbevents.js');
              fbq('init', '2676983045894747');
              fbq('track', 'PageView');
            })()`,
        }}
      />
      <script
        type="text/javascript"
        dangerouslySetInnerHTML={{
          __html: `
          _linkedin_partner_id = "3583161";
          window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
          window._linkedin_data_partner_ids.push(_linkedin_partner_id);
       
          (function(l) {
          if (!l){window.lintrk = function(a,b){window.lintrk.q.push([a,b])};
          window.lintrk.q=[]}
          var s = document.getElementsByTagName("script")[0];
          var b = document.createElement("script");
          b.type = "text/javascript";b.async = true;
          b.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js";
          s.parentNode.insertBefore(b, s);})(window.lintrk);`,
        }}
      />
      <script
        type="text/javascript"
        dangerouslySetInnerHTML={{
          __html: `
          !function(q,e,v,n,t,s){if(q.qp) return; n=q.qp=function(){n.qp?n.qp.apply(n,arguments):n.queue.push(arguments);}; n.queue=[];t=document.createElement(e);t.async=!0;t.src=v; s=document.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t,s);}(window, 'script', 'https://a.quora.com/qevents.js');
          qp('init', 'd8eb09b79e1e4535a6dcf54d3c33e59d');
          qp('track', 'ViewContent');`,
        }}
      />
      <noscript>
        <img
          height="1"
          width="1"
          style={{ display: "none" }}
          src="https://q.quora.com/_/ad/d8eb09b79e1e4535a6dcf54d3c33e59d/pixel?tag=ViewContent&noscript=1"
        />
      </noscript>
      <script
        type="text/javascript"
        dangerouslySetInnerHTML={{
          __html: `
                !function(w,d){if(!w.rdt){var p=w.rdt=function(){p.sendEvent?p.sendEvent.apply(p,arguments):p.callQueue.push(arguments)};p.callQueue=[];var t=d.createElement("script");t.src="https://www.redditstatic.com/ads/pixel.js",t.async=!0;var s=d.getElementsByTagName("script")[0];s.parentNode.insertBefore(t,s)}}(window,document);`,
        }}
      />
      <script
        type="text/javascript"
        dangerouslySetInnerHTML={{
          __html: `!function(e,t,n,s,u,a){e.twq||(s=e.twq=function(){s.exe?s.exe.apply(s,arguments):s.queue.push(arguments);
          },s.version='1.1',s.queue=[],u=t.createElement(n),u.async=!0,u.src='https://static.ads-twitter.com/uwt.js',
          a=t.getElementsByTagName(n)[0],a.parentNode.insertBefore(u,a))}(window,document,'script');
          twq('config','obo6d');`,
        }}
      />
    </>
  );
};

const OurScripts = ({ webConfig }: { webConfig: WebConfig }) => {
  return (
    <>
      <script src="https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js" />
      <script src="https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js" />
      <script
        type="text/javascript"
        src="https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js"
      />
      <WebConfigScript webConfig={webConfig} />
    </>
  );
};

const WebConfigScript = ({ webConfig }: { webConfig: WebConfig }) => (
  <script
    dangerouslySetInnerHTML={{
      __html: `window.webConfig = ${JSON.stringify(webConfig)}`,
    }}
  />
);
